import { PageHeader } from "@shared/components/PageHeader";
import { Stepper } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { ArrowLeft } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { WorkflowInfoStep } from "./steps/workflowInfoStep";
import { SetupActionsStep } from "./steps/setupActionStep";
import { ReviewInfoStep } from "./steps/reviewInfoStep";

interface CreateWorkflowProps {
  editMode?: boolean;
  initialData?: Partial<any>;
  workflowId?: string;
}

const CreateWorkflow: React.FC<CreateWorkflowProps> = ({
  editMode = false,
  initialData = {},
  workflowId,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [globalFormData, setGlobalFormData] = useState<any>({
    workflowName: "",
    description: "",
    audienceType: "",
    actionType: "",
    segment: "",
    recipientList: "",
    scheduleType: "",
    scheduleDate: "",
    scheduleTime: "",
    frequency: "",
    startDate: "",
    endDate: "",
    time: "",
    triggerEvent: "" ,
    ...initialData,
  });

  const updateGlobalFormData = (data: any) => {
    setGlobalFormData((prev) => ({ ...prev, ...data }));
  };

  const handleComplete = async (allStepData: any) => {
    try {
      const finalData = {
        ...globalFormData,
        // Merge any additional step data if needed
      };

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast({
        title: editMode ? "Workflow Updated" : "Workflow Created",
        description: editMode
          ? `Workflow "${finalData.workflowName}" updated successfully`
          : `Workflow "${finalData.workflowName}" created successfully`,
      });

      // Redirect after success
      setTimeout(() => {
        window.location.href = "/workflows";
      }, 1500);
    } catch (error) {
      toast({
        title: "Error",
        description: editMode
          ? "Failed to update workflow"
          : "Failed to create workflow",
        variant: "destructive",
      });
    }
  };

  const stepperSteps = [
    {
      id: "workflow-details",
      title: "Workflow Details",
      component: (
        <WorkflowInfoStep
          globalFormData={globalFormData}
          updateGlobalFormData={updateGlobalFormData}
          editMode={editMode}
        />
      ),
      optional: false,
      slug: "workflow-details",
    },
    {
      id: "setup-actions",
      title: "Setup Actions",
      component: <SetupActionsStep />,
      optional: false,
      slug: "setup-actions",
    },
    {
      id: "review",
      title: "Review",
      component: <ReviewInfoStep />,
      optional: false,
      slug: "review",
    },
  ];

  return (
    <div className="p-2">
      <div className="space-y-6">
        <div className="mb-4 flex items-center gap-4">
          {/* Back Arrow */}
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/communication-hub/workflows")}
          />

          {/* Page Header */}
          <div className="flex flex-col">
            <PageHeader
              title={editMode ? "Edit Workflow" : "Create Workflow"}
              subtitle={
                editMode
                  ? `Update workflow: ${
                      workflowId || globalFormData.workflowName
                    }`
                  : "Set up a new automated workflow for your communication needs"
              }
            />
          </div>
        </div>

        <Stepper
          steps={stepperSteps}
          onComplete={handleComplete}
          autoSave={true}
          persistProgress={true}
          enableApiSave={false}
          saveOnStepChange={false}
          trackDirtyState={false}
          storageKey={
            editMode ? `edit-workflow-${workflowId}` : "add-workflow-progress"
          }
          allowSkip={false}
          enableRouting={true}
          className="w-full"
          allowStepClick={false}
          onStepChange={undefined}
          onValidation={undefined}
          onStepSave={undefined}
        />
      </div>
    </div>
  );
};

export default CreateWorkflow;
